import type { ProjectState } from "@livcg/schema";
import { useLiveClock } from "../../shared/liveClock.js";
import { Badge, cn, StatusDot } from "../../ui/index.js";
import { overlayModeLabel } from "../overlayModes.js";
import { PHASE_SHORT } from "../phases.js";

function pad(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

/** At-a-glance broadcast monitor line — the operator should never have to
 *  hunt for "are we live, what's the time, what's on air". Read-only; all
 *  the controls live in the rails below. */
export function StatusStrip({ slug, state }: { slug: string; state: ProjectState }) {
  // The clock is a separate subscription — the tick re-renders this strip, not the
  // whole Control Room (shared/liveClock.ts).
  const m = useLiveClock(slug);
  const onAir = state.overlay.mode;
  const home = state.teams.home;
  const away = state.teams.away;

  return (
    <div className="flex items-center gap-4 border-b border-divider bg-surface/40 px-4 py-2">
      {/* Live tally */}
      <div className="flex items-center gap-2">
        <StatusDot tone={m.running ? "live" : "idle"} pulse={m.running} />
        <span
          className={cn(
            "text-[11px] font-semibold uppercase tracking-[0.12em]",
            m.running ? "text-live" : "text-ink-muted",
          )}
        >
          {m.running ? "Live" : "Pauza"}
        </span>
      </div>

      <span className="rounded bg-surface-elevated px-1.5 py-0.5 font-mono text-[11px] font-medium text-ink-soft">
        {PHASE_SHORT[m.phase]}
      </span>

      {/* Clock mirror */}
      <span className="font-display text-[18px] font-bold tabular-nums tracking-[-0.02em] text-ink">
        {pad(m.minute)}
        <span className={m.running ? "text-live" : "text-ink-faint"}>:</span>
        {pad(m.second)}
        {m.additionalTime > 0 && (
          <span className="ml-1 text-[12px] font-medium text-paused">+{m.additionalTime}</span>
        )}
      </span>

      {/* Score */}
      <div className="flex items-center gap-2 text-[13px]">
        <span className="font-medium text-ink-soft">{home?.shortName ?? "HOM"}</span>
        <span className="font-display text-[16px] font-bold tabular-nums text-ink">
          {state.match.homeScore}–{state.match.awayScore}
        </span>
        <span className="font-medium text-ink-soft">{away?.shortName ?? "AWY"}</span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {onAir ? (
          <Badge tone="live" dot pulse>
            Efirda · {overlayModeLabel(onAir)}
          </Badge>
        ) : (
          <Badge tone="neutral">Efirda overlay yo'q</Badge>
        )}
      </div>
    </div>
  );
}
